import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
// import Checkbox from '../../../components/forms/Checkbox';
import Checkbox from "../../customize-button/components/form/Checkbox";
import FilterItem from '../FilterItem';
import { splitEvery, deepEqual, scrollToResults, isMobile } from '../../../utilities/helpers';
import { updateBudgetFilter, updateMainFilter } from '../mainFilterSlice';
import { selectSecondaryFilter, updateSecondaryFilter } from '@/features/secondaryFilter/secondaryFilterSlice';
import { useDispatch, useSelector } from 'react-redux';
import { t } from '../../../utilities/translator';
import { pushEvent } from '../../../utilities/analytics';
import { WHAT_FILTER_UPDATE_EVENT } from '../../../constants/analyticEvents';
import { clearSelectedHotel } from '../../search/searchSlice';
import { filterClearClick, filterSaveClick, setLastChangedFilter } from '../../../appSlice';
import useHandleMainFilterState from '../../../hooks/mainFilter/useHandleMainFilterState';
import { selectFilterData } from '../selectors';
import useSearchPerformed from '../../../hooks/useSearchPerformed';
import { getParamFromUrl, removeParamFromUrl } from '../../../utilities/urlResolver';
import { useGetWhatByCapacity } from '../../../hooks/mainFilter/useGetWhatByCapacity';
import { MARKET } from '../../../constants';

function VacationType() {
  const dispatch = useDispatch();
  const clearTimerRef = useRef(null);
  const saveTimerRef = useRef(null);
  const { searchEngine } = useSelector(selectFilterData);
  const { data: secondaryFilter } = useSelector(selectSecondaryFilter);
  const what = useGetWhatByCapacity(searchEngine.who);
  const { where, budget } = searchEngine;

  const [isClean, setIsClean] = useState(false);
  const [checkboxes, setCheckboxes] = useState(what);
  const [allTypesChecked, setAllTypesChecked] = useState(false);
  const [label, setLabel] = useState('');
  const showClearButton = what.some((item) => item.active);

  const [setAppTimeout] = useSearchPerformed();

  useEffect(() => {
    return () => {
      clearTimeout(clearTimerRef.current);
      clearTimeout(saveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const getLabelValue = (data) => {
      return data
        .filter((item) => item.active)
        .map((item) => item.value)
        .join(', ');
    };

    setLabel(getLabelValue(what));
    setCheckboxes(what);
  }, [what]);

  useEffect(() => {
    setIsClean(allTypesChecked ? false : deepEqual(checkboxes, what));
  }, [checkboxes, what, allTypesChecked]);

  const [isOpen, setIsOpen] = useHandleMainFilterState({ filter: 'what' });

  const clearWhereFilter = useCallback(() => {
    const updatedValue = {
      destinations: where.destinations.map((item) => ({
        ...item,
        active: false,
      })),
      destinationsGroups: where.destinationsGroups.map((item) => ({
        ...item,
        active: false,
      })),
    };

    dispatch(
      updateMainFilter({
        filter: 'where',
        updatedValue,
      })
    );
  }, [dispatch, where]);

  const handleCheckboxChange = (id) => {
    let data = checkboxes.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          active: !item.active,
        };
      }

      return item;
    });

    setAllTypesChecked(false);
    setCheckboxes(data);
  };

  const isStateEqual = deepEqual(checkboxes, what);

  const handleSaveClick = useCallback(() => {
    if (allTypesChecked || !isStateEqual) {
      dispatch(filterSaveClick());
      saveTimerRef.current = setTimeout(() => {
        dispatch(
          updateMainFilter({
            filter: 'what',
            updatedValue: checkboxes,
          })
        );
        dispatch(clearSelectedHotel());
        dispatch(setLastChangedFilter('what'));
        clearWhereFilter();

        // Specific logic for budget based theme
        const selected82 = checkboxes.find((item) => [82, 88].includes(item.id))?.active;
        const selected83 = checkboxes.find((item) => item.id === 83)?.active;

        if (what.find(item => [82, 88].includes(item.id))?.active && !selected82 && MARKET === 1) {
          dispatch(updateBudgetFilter([100, 3000]));
        }
        if (what.find(item => item.id === 83)?.active && !selected83) {
          dispatch(updateBudgetFilter([100, 3000]));
        }
        if (selected82 && MARKET === 1) {
          dispatch(updateBudgetFilter([budget.initial.min, 399]))
        }
        if (selected83) {
          dispatch(updateBudgetFilter([budget.initial.min, 499]))
        }
      }, 0);
      pushEvent(WHAT_FILTER_UPDATE_EVENT, { whatFilter: checkboxes, searchEngine });
      setAppTimeout(7000);
      setIsOpen(false);
      if (getParamFromUrl('mainDestinationId')) {
        removeParamFromUrl(['mainDestinationId']);
      }
      if (isMobile()) {
        scrollToResults();
      }
    }
  }, [allTypesChecked, checkboxes, clearWhereFilter, dispatch, isStateEqual, searchEngine, setAppTimeout, setIsOpen]);

  const handleAllTypesCheck = () => {
    setAllTypesChecked(!allTypesChecked);
    setCheckboxes(checkboxes.map((item) => ({ ...item, active: false })));
  };

  const handleClearClick = useCallback(() => {
    if (isStateEqual && what.filter((item) => item.active).length === 0) return;
    dispatch(filterClearClick());
    setIsClean(true);
    setLabel('');
    scrollToResults();
    // Specific logic for budget based theme
    if (checkboxes.find(item => item.id === 82)?.active) {
      if (budget.min === 100 && budget.max === 399) {
        dispatch(updateBudgetFilter([100, 3000]));
      }
    };

    if (checkboxes.find(item => item.id === 83)?.active) {
      if (budget.min === 100 && budget.max === 499) {
        dispatch(updateBudgetFilter([100, 3000]));
      }
    }

    const selectedAmenities = checkboxes.filter(item => (item?.included_facilities?.length > 0 && item.active));
    if (selectedAmenities?.length) {
      // clear facilities filter if what filter had included_facilities
      const { facilities } = secondaryFilter;
      const updatedLocalState = facilities?.map((facility) => {
        return selectedAmenities.find((filter) => filter.included_facilities?.includes(+facility.value))
          ? { ...facility, active: false }
          : facility;
      });
  
      dispatch(
        updateSecondaryFilter({
          updatedValue: updatedLocalState,
          filter: 'facilities',
          noChangeLastChanged: true,
        })
      );
    }

    clearTimerRef.current = setTimeout(() => {
      clearWhereFilter();
      dispatch(
        updateMainFilter({
          filter: 'what',
          updatedValue: checkboxes.map((item) => ({ ...item, active: false })),
        })
      );
    }, 0);
  }, [checkboxes, clearWhereFilter, dispatch, isStateEqual, what]);

  const onClickOutside = useCallback(() => {
    setCheckboxes(what);
    if (isMobile()) {
      scrollToResults();
    }
  }, [what]);

  const checkboxList = splitEvery(checkboxes, Math.ceil((checkboxes.length + 1) / 2), {}).map((chunk, i) => (
    <div className="col-lg-6 col-md-6 flex flex-col gap-3" key={i}>
      {chunk.map((item, j) => {
        if (i === 0 && j === 0) {
          return (
            <Checkbox
              key="all types"
              label={t('mainFilter.all_vacation')}
              checked={allTypesChecked}
              onChange={handleAllTypesCheck}
            />
          );
        }
        return (
          <Checkbox
            key={item.id}
            label={item.value}
            checked={item.active}
            onChange={() => handleCheckboxChange(item.id)}
          />
        );
      })}
    </div>
  ));

  return (
    <FilterItem
      label={t('mainFilter.What')}
      placeholder={t('mainFilter.all_vacation')}
      value={label}
      onSave={handleSaveClick}
      onClear={handleClearClick}
      icon="ri-suitcase-2-line"
      eventName="what"
      id="what"
      showClearButton={showClearButton}
      additionalClass="vacation-type"
      clean={isClean}
      resetOnClickOutside={onClickOutside}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    >
      <div className="main-filter__item-wrap type-filter">
        <div className="c-row">{checkboxList}</div>
      </div>
    </FilterItem>
  );
}

export default React.memo(VacationType);
